import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { PlaceMarker } from '../components/PlaceMarker';
import { fetchPublicMap, type Map } from '../services/apiClient';
import { useMapTheme } from '../hooks/useMapTheme';
import { getTheme } from '../config/theme.config';
import type { Place } from '../types/Place';

/**
 * EmbedPage - Minimal embeddable map view
 *
 * SPRINT-8.5: Embeddable map page
 * - Loads map by public slug from URL
 * - Renders ONLY the map (no header, footer, sidebars)
 * - Supports query params: zoom, lat, lng, hideControls
 * - Full viewport, iframe-friendly
 * - Optional "Powered by Love Map" watermark
 */

// Map theme defaults
const themeMap = getTheme().map;

// Custom cluster icon for embed view
interface ClusterMarker extends L.Marker {
  getLatLng: () => L.LatLng;
}

interface Cluster {
  getChildCount: () => number;
  getAllChildMarkers: () => ClusterMarker[];
}

const createClusterIcon = (cluster: Cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  if (count >= 10) size = 'medium';
  if (count >= 20) size = 'large';

  return L.divIcon({
    html: `<div class="cluster-marker cluster-${size}"><span>${count}</span></div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(40, 40, true),
  });
};

export function EmbedPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [map, setMap] = useState<Map | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse query parameters
  const initialZoom = searchParams.get('zoom')
    ? parseInt(searchParams.get('zoom')!, 10)
    : null;
  const initialLat = searchParams.get('lat')
    ? parseFloat(searchParams.get('lat')!)
    : null;
  const initialLng = searchParams.get('lng')
    ? parseFloat(searchParams.get('lng')!)
    : null;
  const hideControls = searchParams.get('hideControls') === 'true';
  const hideWatermark = searchParams.get('hideWatermark') === 'true';

  // Get theme from map settings
  const mapTheme = map?.settings?.theme;
  const { tileUrl, tileAttribution, cssVariables, isLightTheme } = useMapTheme(mapTheme);

  // Calculate initial center from places or use params
  const initialCenter = useMemo((): [number, number] => {
    if (initialLat !== null && initialLng !== null) {
      return [initialLat, initialLng];
    }
    if (places.length > 0) {
      // Calculate center of all places
      const sumLat = places.reduce((sum, p) => sum + p.coordinates[0], 0);
      const sumLng = places.reduce((sum, p) => sum + p.coordinates[1], 0);
      return [sumLat / places.length, sumLng / places.length];
    }
    return themeMap.defaultCenter;
  }, [places, initialLat, initialLng]);

  // Use initial zoom or calculate from places bounds
  const zoom = initialZoom ?? themeMap.defaultZoom;

  useEffect(() => {
    async function loadPublicMap() {
      if (!slug) {
        setError('Invalid map link');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const data = await fetchPublicMap(slug);
        setMap(data.map);
        setPlaces(data.places);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load map';
        const match = message.match(/API Error (\d+): (.+)/);
        if (match) {
          const statusCode = parseInt(match[1], 10);
          if (statusCode === 404) {
            setError('Map not found');
          } else {
            setError(match[2]);
          }
        } else {
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadPublicMap();
  }, [slug]);

  // Apply theme CSS variables
  const themeStyle = useMemo(() => {
    const style: Record<string, string> = {};
    Object.entries(cssVariables).forEach(([key, value]) => {
      style[key] = value;
    });
    return style;
  }, [cssVariables]);

  // Minimal loading state
  if (isLoading) {
    return (
      <div
        className="h-screen w-screen bg-bg-base flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <div className="w-8 h-8 rounded-full bg-primary/30 animate-pulse" />
      </div>
    );
  }

  // Error state
  if (error || !map) {
    return (
      <div
        className="h-screen w-screen bg-bg-base flex items-center justify-center"
        role="alert"
      >
        <div className="text-center text-gray-400 text-sm">
          <p>{error || 'Map not available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen relative overflow-hidden"
      style={themeStyle}
    >
      <MapContainer
        center={initialCenter}
        zoom={zoom}
        minZoom={themeMap.minZoom}
        maxZoom={themeMap.maxZoom}
        scrollWheelZoom={true}
        touchZoom={true}
        dragging={true}
        zoomControl={!hideControls}
        attributionControl={!hideControls}
        className={`h-full w-full ${isLightTheme ? 'bg-gray-200' : 'bg-bg-base'}`}
      >
        <TileLayer url={tileUrl} attribution={tileAttribution} />
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterIcon}
          maxClusterRadius={themeMap.clusterMaxRadius}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          disableClusteringAtZoom={themeMap.clusterDisableAtZoom}
        >
          {places.map((place) => (
            <PlaceMarker
              key={place.id}
              place={place}
              onClick={() => {}} // No-op for embed view
              isVisible={true}
              isInteractive={false}
              state="default"
            />
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      {/* Powered by Love Map watermark */}
      {!hideWatermark && (
        <div className="absolute bottom-2 right-2 z-[1000] pointer-events-none">
          <div className="bg-bg-surface/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-400 border border-gray-800">
            Powered by Love Map
          </div>
        </div>
      )}
    </div>
  );
}
